export interface APIResponse<T = any> {
    response: {
        result: {
            rows: T[][]
            column_order: string[]
        }
    }
}

export interface AggregatedResult {
    month: string
    value: number
    count?: number
}

export interface BarChartAggregatedResult {
    baselineRow: any[] | null
    latestRow: any[] | null
}

export type AggregationMethod = 'sum' | 'average' | 'count'

export type MonthMode = 'actual_month' | 'recent_month'

export function aggregateMultiSelect(
    apiData: APIResponse,
    method: AggregationMethod,
    targetColumn: string,
    monthMode: MonthMode = 'actual_month'
): AggregatedResult[] {
    const rows = apiData?.response?.result?.rows
    const columns = apiData?.response?.result?.column_order

    if (!rows || !columns) {
        throw new Error('Invalid API data structure')
    }

    const hospitalIndex = columns.indexOf('HospitalId')
    const monthIndex = columns.indexOf('Month')
    const valueIndex = columns.indexOf(targetColumn)

    if (hospitalIndex === -1 || monthIndex === -1 || valueIndex === -1) {
        throw new Error('Required column(s) not found in data')
    }

    if (monthMode === 'recent_month') {
        const latestByHospital = new Map<
            string,
            { value: number; date: Date; month: string }
        >()

        for (const row of rows) {
            const hospitalId = String(row[hospitalIndex])
            const month = String(row[monthIndex])
            const value = parseFloat(String(row[valueIndex])) || 0
            const dateKey = new Date(`1 ${month}`)

            if (
                !latestByHospital.has(hospitalId) ||
                dateKey > latestByHospital.get(hospitalId)!.date
            ) {
                latestByHospital.set(hospitalId, { value, date: dateKey, month })
            }
        }

        const values = Array.from(latestByHospital.values()).map((v) => v.value)
        const monthsUsed = Array.from(latestByHospital.values()).map((v) => v.month)
        const label = monthsUsed.length === 1 ? monthsUsed[0] : 'Mixed'

        let aggregated: number
        if (method === 'sum') {
            aggregated = values.reduce((a, b) => a + b, 0)
        } else if (method === 'average') {
            aggregated = values.reduce((a, b) => a + b, 0) / values.length
        } else if (method === 'count') {
            aggregated = values.length
        } else {
            throw new Error('Unsupported aggregation method: ' + method)
        }

        return [
            {
                month: label,
                value: Math.round(aggregated * 100) / 100,
                count: values.length,
            },
        ]
    }

    const groupedByMonth: Record<string, number[]> = {}

    for (const row of rows) {
        const month = String(row[monthIndex])
        const value = parseFloat(String(row[valueIndex])) || 0

        if (!groupedByMonth[month]) {
            groupedByMonth[month] = []
        }

        groupedByMonth[month].push(value)
    }

    const result = Object.entries(groupedByMonth).map(([month, values]) => {
        let aggregated: number
        if (method === 'sum') {
            aggregated = values.reduce((a, b) => a + b, 0)
        } else if (method === 'average') {
            aggregated = values.reduce((a, b) => a + b, 0) / values.length
        } else if (method === 'count') {
            aggregated = values.length
        } else {
            throw new Error('Unsupported aggregation method: ' + method)
        }

        return {
            month,
            value: Math.round(aggregated * 100) / 100,
        }
    })

    result.sort((a, b) => new Date(`1 ${a.month}`).getTime() - new Date(`1 ${b.month}`).getTime())
    return result
}

export function aggregateBarChartData(
    apiData: APIResponse,
    isMultiSelect: boolean,
    baselineIdentifier: string = 'Baseline'
): BarChartAggregatedResult {
    const rows = apiData?.response?.result?.rows || []
    const columns = apiData?.response?.result?.column_order || []

    if (!isMultiSelect || rows.length === 0) {
        return { baselineRow: null, latestRow: null }
    }

    const baselineRows: any[][] = []
    const latestRows: any[][] = []

    rows.forEach(row => {
        const period = row[columns.indexOf("period")] || row[columns.indexOf("time_period")]
        if (period === baselineIdentifier) {
            baselineRows.push(row)
        } else {
            latestRows.push(row)
        }
    })

    const calculateAverages = (rowsToAverage: any[][], columnNames: string[]): Record<string, number> | null => {
        if (rowsToAverage.length === 0) return null

        const averages: Record<string, number> = {}
        columnNames.forEach(colName => {
            const colIndex = columns.indexOf(colName)
            if (colIndex !== -1) {
                const validValues = rowsToAverage.map(row => {
                    const val = parseFloat(row[colIndex])
                    return isNaN(val) ? null : val
                }).filter(val => val !== null) as number[]

                if (validValues.length > 0) {
                    averages[colName] = validValues.reduce((sum, val) => sum + val, 0) / validValues.length
                } else {
                    averages[colName] = 0
                }
            }
        })
        return averages
    }

    const providerEducationColumns = [
        "percent_ob_education",
        "percent_ob_nurse_education",
        "percent_er_education",
        "percent_er_nurse_education"
    ]

    const screeningColumns = [
        "anxiety_delivery",
        "depression_delivery",
        "anxiety_prenatal",
        "depression_prenatal"
    ]

    const patientEducationColumn = "education_percentage"

    let baselineAverages: Record<string, number> | null = null
    let latestAverages: Record<string, number> | null = null

    if (providerEducationColumns.some(col => columns.includes(col))) {
        baselineAverages = calculateAverages(baselineRows, providerEducationColumns)
        latestAverages = calculateAverages(latestRows, providerEducationColumns)
    } else if (screeningColumns.some(col => columns.includes(col))) {
        baselineAverages = calculateAverages(baselineRows, screeningColumns)
        latestAverages = calculateAverages(latestRows, screeningColumns)
    } else if (columns.includes(patientEducationColumn)) {
        baselineAverages = calculateAverages(baselineRows, [patientEducationColumn])
        latestAverages = calculateAverages(latestRows, [patientEducationColumn])
    }

    const createAggregatedRow = (averages: Record<string, number> | null, period: string): any[] | null => {
        if (!averages) return null

        const row: any[] = []
        columns.forEach(col => {
            if (averages[col] !== undefined) {
                row.push(averages[col])
            } else if (col === "period" || col === "time_period") {
                row.push(period)
            } else {
                row.push(null)
            }
        })
        return row
    }

    return {
        baselineRow: createAggregatedRow(baselineAverages, baselineIdentifier),
        latestRow: createAggregatedRow(latestAverages, "Latest")
    }
}

